<script setup lang="ts">
import * as yup from 'yup'
import SmallModel from "@/components/Interface/Modal/ModelSizes/SmallModel.vue";
import {reactive} from "vue";
import Backend from '@/models/backend.ts'
import FormProcess from '@/models/formProcess.ts'
import FormInput from "@/components/Interface/Modal/Forms/FormInput.vue";
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePQAStore } from '@/stores/PQAStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const PQAStore = usePQAStore()
const backend = new Backend()
const formProcess = new FormProcess()


// Define the validation schema
const schema = yup.object().shape({
  PQAName: yup
    .string()
    .min(3, () => t('modals.validation.minChars', { min: 3 }))
    .max(32, () => t('modals.validation.maxChars', { max: 32 }))
    .test('not-only-numbers', () => t('modals.pqa.validation.notOnlyNumbers'), (value) => !/^\d+$/.test(value || ''))
    .required(() => t('modals.validation.required'))
})

// Default form values
const formValues = reactive<{ [key: string]: string }>({
  PQAName: ''
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: '',
  PQAName: ''
});


// Handle form submission
const submit = async () => {
  try {
    // Validate the form values
    await schema.validate(formValues);
    formProcess.resetFormErrors(formErrors);

    // Create the PQA
    const response = await backend.createPQA(formValues.PQAName);

    // Wait in a loop until the PQA is created and reported
    const waitForPQA = async () => {
      while (!PQAStore.queues.has(response.data.id)) {
        await new Promise((r) => setTimeout(r, 200));
      }
    };

    await waitForPQA();

    // Update the modal type and data
    gameStore.modalType = 'configPQA';
    gameStore.modalData = response.data.id;
  } catch (error:any) {
    if (error.name === 'ValidationError') {
      // Handle frontend validation errors
      formProcess.processFrontendErrorResponse(error, formErrors);
    } else {
      // Handle backend errors
      formProcess.processBackendErrorResponse(error, formErrors);
    }
  }
};


</script>

<template>
  <SmallModel :title="t('modals.pqa.create.title')">
    <form method="post" @submit.prevent="submit" class="p-3 pb-6 md:pb-3">
      <FormInput
          :label="t('modals.pqa.create.nameLabel')"
          v-model="formValues.PQAName"
          :error="formErrors.PQAName"
      />
      <div class="flex justify-between pt-4 md:pt-2">
        <FormGenericError :error="formErrors.generic"/>
        <ActionButton :spinner="true" class="z-10 hidden md:block" @click="submit">{{ t('common.actions.create') }}</ActionButton>
      </div>
    </form>
  </SmallModel>
</template>


<style scoped lang="scss">

</style>